import { Injectable, inject, signal } from '@angular/core';
import { LoggerService } from '@core/logging/logger.service';

/**
 * Storage keys, unchanged from the previous app so a token it stored on this
 * origin is still honoured, and so both apps can run during the transition.
 */
const TOKEN_KEY = 'token';
const USER_KEY = 'currentUser';

export interface DoctorIdentity {
  id: string;
  name: string;
  role: string;
  email?: string;
}

/**
 * The doctor's DoctorConsole JWT.
 *
 * There is no login screen here: the doctor console signs the doctor in and
 * opens `/consultation/{id}#token=…&autoLogin=true`. The token travels in the
 * fragment so it never reaches a server log, and it is removed from the address
 * bar once read so it does not survive in history or a copied link.
 *
 * The patient never has one. Their endpoints are anonymous, and a patient device
 * holding a doctor's credential would be the defect, not the fix.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly logger = inject(LoggerService);

  private readonly current = signal<string | null>(readStored(TOKEN_KEY));
  readonly identity = signal<DoctorIdentity | null>(readStoredIdentity());

  /**
   * Take the token from the URL fragment, if the doctor console put one there.
   *
   * Run once at startup, before routing, because it is the only moment the
   * fragment is guaranteed to still be on the URL.
   */
  captureFromUrl(): void {
    let hash: string;
    try {
      hash = window.location.hash.replace(/^#/, '');
    } catch {
      return;
    }
    if (!hash) return;

    const params = new URLSearchParams(hash);
    const token = params.get('token');
    if (!token || params.get('autoLogin') !== 'true') return;

    const identity = decodeIdentity(token);
    if (!identity) {
      // A token that cannot be read would fail every call it is sent on. Saying
      // so here is the difference between "sign-in link is broken" and a later
      // 401 that looks like a backend fault.
      this.logger.error('backend', 'auth.token.unreadable');
      return;
    }

    this.store(token, identity);
    this.logger.log('backend', 'auth.token.captured', {
      userId: identity.id,
      expired: isExpired(token),
    });

    params.delete('token');
    params.delete('autoLogin');
    const rest = params.toString();
    try {
      history.replaceState(
        history.state,
        '',
        `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`,
      );
    } catch (error) {
      this.logger.warn('backend', 'auth.token.url.clear.failed', {
        error: String(error),
      });
    }
  }

  /** The bearer token, or null when this device has none. */
  token(): string | null {
    return this.current();
  }

  /** True when a token is held and has not expired. */
  hasValidToken(): boolean {
    const token = this.current();
    return token !== null && !isExpired(token);
  }

  private store(token: string, identity: DoctorIdentity): void {
    this.current.set(token);
    this.identity.set(identity);
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(identity));
    } catch (error) {
      // The token still applies for this page; only a reload would lose it.
      this.logger.warn('backend', 'auth.token.persist.failed', {
        error: String(error),
      });
    }
  }
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readStoredIdentity(): DoctorIdentity | null {
  const raw = readStored(USER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DoctorIdentity>;
    return parsed && parsed.id ? (parsed as DoctorIdentity) : null;
  } catch {
    return null;
  }
}

function decodePayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      Array.from(atob(base64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** The claims the previous app read, in the order it read them. */
function decodeIdentity(token: string): DoctorIdentity | null {
  const payload = decodePayload(token);
  if (!payload) return null;

  const id = payload['UserId'] ?? payload['nameid'];
  if (id === undefined || id === null || id === '') return null;

  return {
    id: String(id),
    name: typeof payload['unique_name'] === 'string' ? payload['unique_name'] : 'Doctor',
    role: typeof payload['role'] === 'string' ? payload['role'] : 'Doctor',
    email: typeof payload['email'] === 'string' ? payload['email'] : undefined,
  };
}

function isExpired(token: string): boolean {
  const exp = decodePayload(token)?.['exp'];
  return typeof exp === 'number' ? exp * 1000 <= Date.now() : false;
}
